// İSG uzmanı portalı — Risk Değerlendirme enum yardımcıları (durum / seviye / DÖF durumu +
// 5x5 skor türetimi). Wire değerleri api-contract §19'dan; çeviri (etiket) çağıran ekrandadır
// (i18n key = `risk.status.<value>` / `risk.level.<value>` / `risk.action.status.<value>`).
// Durum `Tag` rengi (S33 Slice 4 — eski `Badge` kaldırıldı, paylaşılan `Tag`); seviye rozeti
// ayrı (RiskLevelBadge, ısı skalası token'ları — dokunulmaz).
//
// **⛔ Skor/seviye SUNUCU otoritesidir (§19).** `deriveScore`/`deriveLevel` YALNIZ matris
// seçicide **canlı GÖSTERİM** içindir (kullanıcı Olasılık/Şiddet seçerken anlık geri bildirim).
// API'ye YALNIZ `probability`/`severity` (+ residual) gönderilir; kalıcı `score`/`level`/
// `highestLevel` **her zaman sunucudan gelen değerdir** (istemci yeniden hesaplamaz).

import type {
  RiskActionDto,
  RiskActionStatus,
  RiskAssessmentStatus,
  RiskLevel,
  TagColor,
} from '@isgportal/shared';
import { calendarDayDiff } from '../../lib/format';

/** Değerlendirme durumu seçenekleri — filtre/gösterim (wire PascalCase; §19). */
export const RISK_STATUSES: RiskAssessmentStatus[] = [
  'Draft',
  'Submitted',
  'Approved',
  'Active',
  'UnderReview',
  'Revised',
  'Archived',
];

/** 5x5 band seçenekleri — filtre/gösterim (en düşükten en yükseğe; ASCII wire; §19). */
export const RISK_LEVELS: RiskLevel[] = ['Onemsiz', 'Dusuk', 'Orta', 'Yuksek', 'KabulEdilemez'];

/** DÖF durumu seçenekleri — form/gösterim (wire PascalCase; §19). */
export const RISK_ACTION_STATUSES: RiskActionStatus[] = ['Open', 'InProgress', 'Done', 'Cancelled'];

/** Olasılık/Şiddet faktör aralığı (§19: 1-5). */
export const RISK_FACTORS = [1, 2, 3, 4, 5] as const;

/**
 * Değerlendirme durumu `Tag` rengi (§19): `Active` yeşil (yürürlükte), `Approved` mavi (onaylı
 * — eski turuncu "accent"in göç hedefi), `Submitted`/`UnderReview` gri (onay/gözden geçirme
 * bekliyor), `Draft`/`Revised`/`Archived` gri (taslak / revize / terminal-arşiv).
 */
export function riskStatusTone(status: RiskAssessmentStatus): TagColor {
  switch (status) {
    case 'Active':
      return 'green';
    case 'Approved':
      return 'blue';
    default:
      return 'gray';
  }
}

/**
 * DÖF durumu `Tag` rengi (§19): `Done` yeşil (tamamlandı), `InProgress` gri (sürüyor),
 * `Cancelled` kırmızı (iptal), `Open` gri (açık).
 */
export function riskActionStatusTone(status: RiskActionStatus): TagColor {
  switch (status) {
    case 'Done':
      return 'green';
    case 'Cancelled':
      return 'red';
    default:
      return 'gray';
  }
}

/**
 * DÖF **gecikmiş mi** (S36 #3): `dueDate`in yerel takvim günü bugünden ÖNCE VE durum
 * `Done`/`Cancelled` DEĞİL. `dueDate` yerel gece yarısının UTC ISO'sudur
 * (`RiskActionFormModal.tsx`) — ham zaman damgası karşılaştırması vade GÜNÜNÜN KENDİSİNDE
 * yanlış "gecikmiş" verirdi; `calendarDayDiff` takvim günü kıyaslar (`ActionItemsPanel.tsx`ın
 * aynı sorununun ortak çözümü, S36 #4).
 */
export function isRiskActionOverdue(action: Pick<RiskActionDto, 'dueDate' | 'status'>): boolean {
  if (!action.dueDate) {
    return false;
  }
  if (action.status === 'Done' || action.status === 'Cancelled') {
    return false;
  }
  return calendarDayDiff(action.dueDate) < 0;
}

/**
 * Değerlendirme **düzenlenebilir mi** (§19): `Draft`/`Submitted`/`UnderReview`/`Revised`.
 * Kilitli (salt-okunur): `Approved`/`Active`/`Archived` → form/kalem/DÖF yazımı gizli (BE
 * `409 RISK_ASSESSMENT_NOT_EDITABLE`'ı UI'da önle). Status'u backend döndürür — UI kendi hesaplamaz.
 */
export function isRiskAssessmentEditable(status: RiskAssessmentStatus): boolean {
  return (
    status === 'Draft' || status === 'Submitted' || status === 'UnderReview' || status === 'Revised'
  );
}

/**
 * **GÖSTERİM-YALNIZ** skor = Olasılık × Şiddet (matris seçici canlı geri bildirimi). Geçerli
 * faktörler 1-5; aralık dışı `null` (henüz seçilmemiş). API'ye GÖNDERİLMEZ — sunucu türetir (§19).
 */
export function deriveScore(
  probability: number | null | undefined,
  severity: number | null | undefined,
): number | null {
  if (
    probability == null ||
    severity == null ||
    probability < 1 ||
    probability > 5 ||
    severity < 1 ||
    severity > 5
  ) {
    return null;
  }
  return probability * severity;
}

/**
 * **GÖSTERİM-YALNIZ** 5x5 L-tipi band eşiği (§19; plan-risk-assessment bağlayıcı): `score ≤ 2`
 * Onemsiz · `≤ 6` Dusuk · `≤ 12` Orta · `≤ 20` Yuksek · `> 20` KabulEdilemez. Kalıcı band
 * **her zaman sunucudan** gelir; bu yalnız matris seçicide anlık rozet içindir.
 */
export function deriveLevel(score: number | null): RiskLevel | null {
  if (score == null) {
    return null;
  }
  if (score <= 2) {
    return 'Onemsiz';
  }
  if (score <= 6) {
    return 'Dusuk';
  }
  if (score <= 12) {
    return 'Orta';
  }
  if (score <= 20) {
    return 'Yuksek';
  }
  return 'KabulEdilemez';
}
